import { Component, inject, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TableColumn } from '../../../../../models/compliance-item.model';
import { Router } from '@angular/router';
import { TripChecklist } from '../../../../../models/inspection-checklist.model';
import { RoutineVideo } from '../../../../../models/routine-video.model';
import { ComplianceOverviewService } from '../../../../../services/compliance-overview.service';
import { ComplianceTasksTableComponent } from '../../../../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { RoutineVideoCardsComponent } from '../routine-video-cards/routine-video-cards.component';
import { VideoViewModalComponent } from '../../../../../../_shared/components/modals/video-view-modal/video-view-modal.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-post-routine-type-checklist',
  standalone: true,
  imports: [
    ComplianceTasksTableComponent,
    RoutineVideoCardsComponent
  ],
  templateUrl: './post-routine-type-checklist.component.html',
  styleUrl: './post-routine-type-checklist.component.scss'
})
export class PostRoutineTypeChecklistComponent {
  private router: Router = inject(Router);
  private modalService: NgbModal = inject(NgbModal);
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private transloco: TranslocoService = inject(TranslocoService);

  @Input() assetName: string = '';
  @Input() assetId: number | null = null;
  @Input() complianceId: number | null = null;
  @Input() routineTypeId: number | null = null;
  @Input() inspectionTypeId: number | null = null;
  @Input() routineComplianceTaskId: number | null = null;
  @Input() routineChecklistTypeLabel: string = '';
  @Input() details: TripChecklist[] = [];
  @Input() videos: RoutineVideo[] = [];
  @Input() videosLoading: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() hasError: boolean = false;

  columns: TableColumn[] = [
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.checklistCategory'), value: 'checklist', width: 'col-2', type: 'text', truncate: true, title: true, bold: true },
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.routineType'), value: 'checklistTypeLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.date'), value: 'date', width: 'col', type: 'date' },
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.documentChecklist'), value: 'docChecklistLabel', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.inspectionUploads'), value: 'inspectionUploads', width: 'col', type: 'text' },
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.notes'), value: 'notes', width: 'col', type: 'text', truncate: true, title: true, mutedValue: this.transloco.translate('compliance.overview.routineDetails.noNoteProvided') },
    { header: this.transloco.translate('compliance.overview.routineDetails.columns.taskStatus'), value: 'taskStatus', width: 'col', type: 'status' },
  ];

  onRowClick(item: TripChecklist) {
    const checklistId = item.checklistId ?? item.id;
    this.router.navigate(['/asset-management/compliance-overview/asset-routine-compliance-details/inspection-checklist'], {
      queryParams: {
        checklist: item.checklist,
        assetName: this.assetName,
        tab: 'post',
        submissionId: item.id,
        checklistId,
        ...(this.assetId != null ? { assetId: this.assetId } : {}),
        ...(this.complianceId != null ? { complianceId: this.complianceId } : {}),
        ...(this.routineTypeId != null ? { routineTypeId: this.routineTypeId } : {}),
        ...(this.routineComplianceTaskId != null ? { routineComplianceTaskId: this.routineComplianceTaskId } : {}),
        ...(this.routineChecklistTypeLabel.trim()
          ? { checklistType: this.routineChecklistTypeLabel.trim() }
          : {}),
        ...(item.docChecklist?.trim()
          ? { checklistTypeName: item.docChecklist.trim() }
          : {}),
      }
    });
  }

  onActionClick(item: any) {
  }

  onVideoClick(video: RoutineVideo): void {
    if (!video.videoUrl || this.routineComplianceTaskId == null) {
      return;
    }

    const taskId = this.routineComplianceTaskId;
    const modalRef = this.modalService.open(VideoViewModalComponent, {
      centered: true,
      size: 'lg'
    });

    modalRef.componentInstance.title = video.title;
    modalRef.componentInstance.videoName = video.title;
    modalRef.componentInstance.videoUrl = video.videoUrl;
    modalRef.componentInstance.downloadHandler = async () => {
      const response = await this.complianceOverviewService.getSubmissionVideoDownloadLinkAsync(
        taskId, video.inspectionTypeId, video.submissionVideoKind);
      return response.data ?? null;
    };
  }
}